import React from 'react';
import { motion } from 'framer-motion';
import { useGradientAnimation } from '@/hooks/useGradientAnimation';
import { ArrowRight } from 'lucide-react';
import { useImageLoading } from '../hero/hooks/useImageLoading';
import { useRotatingText } from '../hero/hooks/useRotatingText';
import { textGradient } from '../hero/styles/gradients';
import { HeroImage } from '../hero/styles/HeroImage';

interface IrreplacableHeroProps {
  onGetStarted: () => void;
}

// Hero image constants - edit these to change the hero image
const HERO_GRAPHIC_URL = "https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/herophoto.webp?alt=media&token=4ee0819b-ca44-4163-b358-469a66176505";
const HERO_GRAPHIC_ALT = "Professional feedback and career growth illustration";

// Headline text - edit this to change the main headline
const HEADLINE_TEXT = "Facing Job Uncertainty?\nWe got you";

// Rotating texts - edit this array to change the rotating texts
const ROTATION_TEXTS = [
  'Pay What You Can, Even Nothing',
  'Keep Evolving',
  'Reaffirm Your Talent',
  'You Are Irreplaceable'
];

// Button text - edit this to change the button text
const BUTTON_TEXT = "Start My Free Growth Journey";

// Subheadline text - edit this to change the text below the headline
const SUBHEADLINE_TEXT = "Gain 360° insights from peers, managers, and stakeholders. Identify strengths, close gaps, and advance confidently into your next role";

// Stats text - edit these to change the stats text
const STATS_COUNT = "500";
const STATS_TEXT = "professionals";

export default function IrreplacableHero({ onGetStarted }: IrreplacableHeroProps) {
  const gradientStyle = useGradientAnimation();
  const { handleLoad, isLoaded } = useImageLoading();
  const { text, isAnimating } = useRotatingText({
    texts: ROTATION_TEXTS
  });

  return (
    <div className="relative bg-gradient-to-b from-dark-lighter to-dark pt-32 sm:pt-28 md:pt-36 pb-12">
      {/* Background elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ duration: 1 }}
        className="absolute inset-0 overflow-hidden"
      >
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=2850&q=80')] bg-cover bg-center opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-dark-lighter to-dark opacity-90" />
      </motion.div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center space-x-2 mb-6"
          >
            <div className="flex items-center space-x-2 pt-[3px]">
              <svg className="w-5 h-5 text-[#f9c55f]" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm font-medium text-gray-300">
                Trusted by {STATS_COUNT}+ {STATS_TEXT}
              </span>
            </div>
          </motion.div>
          
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between lg:gap-12 min-h-[400px]">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center lg:text-left lg:flex-1"
            >
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 lg:text-left"
              >
                <div className="mb-4">
                  {HEADLINE_TEXT.split('\n').map((line, index) => (
                    <div key={index} className={index > 0 ? "mt-2" : ""}>
                      {line}
                    </div>
                  ))}
                </div>
                <div 
                  className={`transition-all duration-500 min-h-[1.2em] ${
                    isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
                  }`}
                >
                  <span className={textGradient}>
                    {text}
                  </span>
                </div>
              </motion.h1>
              
              <div className="block lg:hidden mt-8 mb-8">
                <div className="relative w-full h-full flex items-center">
                  <div className="relative z-10 w-full flex items-center">
                    <HeroImage
                      src={HERO_GRAPHIC_URL}
                      alt={HERO_GRAPHIC_ALT}
                      isLoaded={isLoaded}
                      onLoad={handleLoad}
                    />
                  </div>
                  
                  {/* Decorative gradients */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl blur-2xl opacity-30" />
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-secondary/5 rounded-3xl blur-xl opacity-40" />
                </div>
              </div>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg sm:text-xl text-gray-300 lg:text-left max-w-xl mx-auto lg:mx-0"
              >
                {SUBHEADLINE_TEXT}
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-8 flex justify-center lg:justify-start"
              >
                <button 
                  onClick={onGetStarted}
                  className="group bg-[#ee99ea] text-dark px-8 py-3 rounded-lg hover:bg-[#f0a5ec] transition-all duration-300 transform hover:-translate-y-1 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <span className="font-medium">{BUTTON_TEXT}</span>
                  <ArrowRight className="w-4 h-4 text-[#151b59] group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="hidden lg:flex lg:flex-1 lg:items-center h-full"
            >
              <div className="relative w-full h-full flex items-center">
                <div className="relative z-10 w-full flex items-center">
                  <HeroImage
                    src={HERO_GRAPHIC_URL}
                    alt={HERO_GRAPHIC_ALT}
                    isLoaded={isLoaded}
                    onLoad={handleLoad}
                  />
                </div>
                
                {/* Decorative gradients */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl blur-2xl opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-secondary/5 rounded-3xl blur-xl opacity-40" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}